import type { NextRouter } from 'next/router';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import { RoutingProps } from '../routing.type';

type NextRouterWindow = typeof window & {
  next: { router: NextRouter };
};

async function smartNavigate(url: string, options?: { replace?: boolean }) {
  try {
    if (options?.replace) {
      await (window as NextRouterWindow).next.router.replace(url);
    } else {
      await (window as NextRouterWindow).next.router.push(url);
    }
  } catch {
    location.assign(url);
  }
}

const RoutingContext = createContext<RoutingProps>({
  linkComponent: 'a',
  navigate: smartNavigate,
});

export const RoutingProvider = ({
  linkComponent = 'a',
  navigate = smartNavigate,
  children,
}: {
  linkComponent?: RoutingProps['linkComponent'];
  navigate?: RoutingProps['navigate'];
  children: ReactNode;
}) => {
  const routingProps = useMemo(
    () => ({
      linkComponent,
      navigate,
    }),
    [linkComponent, navigate],
  );

  return <RoutingContext.Provider value={routingProps}>{children}</RoutingContext.Provider>;
};

export function useRouting() {
  const { linkComponent, navigate } = useContext(RoutingContext);

  return {
    linkComponent: linkComponent as Exclude<typeof linkComponent, undefined>,
    navigate: navigate as Exclude<typeof navigate, undefined>,
  };
}
